import React, { useState, useEffect } from 'react';
import { Check, Search, Users, MessageCircle, X, BookMarked } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { useChats } from '@/hooks/useChats';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';

interface Follower {
  id: string;
  username: string;
  name?: string;
  image?: string;
}

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  shareData?: {
    kind: 'book' | 'review';
    book_id?: string;
    review_id?: string;
    book?: {
      id: string;
      title: string;
      authors: string[];
      cover_url?: string;
    };
  };
  mode?: 'share' | 'new-chat';
}

export const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  shareData,
  mode = 'share'
}) => {
  const { user } = useAuth();
  const { chats, createDMChat, createGroupChat } = useChats();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [followers, setFollowers] = useState<Follower[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedChat, setSelectedChat] = useState<string>('');
  const [groupTitle, setGroupTitle] = useState('');
  const [isGroup, setIsGroup] = useState(false);
  const [isBookClub, setIsBookClub] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('direct');

  useEffect(() => {
    if (isOpen && user) {
      fetchFollowers();
    }
  }, [isOpen, user]);

  const fetchFollowers = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('follows')
        .select('following_id')
        .eq('follower_id', user.id)
        .eq('status', 'accepted');

      if (error) throw error;

      if (data && data.length > 0) {
        const userIds = data.map(follow => follow.following_id);
        
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, username, name, image')
          .in('id', userIds);
          
        if (profilesError) throw profilesError;
        
        setFollowers(profilesData || []);
      }
    } catch (error) {
      console.error('Error fetching followers:', error);
    }
  };

  const getOtherUserName = (chat: any) => {
    const otherMember = chat.members?.find((m: any) => m.user_id !== user?.id);
    return otherMember?.user?.name || otherMember?.user?.username;
  };

  const filteredFollowers = followers.filter(follower =>
    follower.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    follower.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredChats = chats.filter(chat => {
    const title = chat.is_group ? chat.title : getOtherUserName(chat);
    return title?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleUserToggle = (userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleShare = async () => {
    if (!user) return;

    setLoading(true);
    try {
      let chatId: string;

      if (activeTab === 'existing') {
        chatId = selectedChat;
      } else {
        // Create new chat
        if (isGroup || selectedUsers.length > 1) {
          const title = groupTitle.trim() || 'New Group';
          chatId = await createGroupChat(title, selectedUsers, isBookClub);
        } else if (selectedUsers.length === 1) {
          chatId = await createDMChat(selectedUsers[0]);
        } else {
          throw new Error('Please select at least one person');
        }
      }

      // Send share message if sharing content
      if (shareData && mode === 'share') {
        await supabase.from('messages').insert([{
          chat_id: chatId,
          sender_id: user.id,
          type: 'share',
          share_payload: shareData,
        }]);

        // Create share record
        await supabase.from('shares').insert([{
          chat_id: chatId,
          sender_id: user.id,
          kind: shareData.kind,
          book_id: shareData.book_id,
          review_id: shareData.review_id,
        }]);

        // Check if this is a book club chat and add to suggestions
        const { data: chatData } = await supabase
          .from('chats')
          .select('is_book_club')
          .eq('id', chatId)
          .single();
        
        if (chatData?.is_book_club && shareData.kind === 'book' && shareData.book_id) {
          try {
            await supabase.from('chat_book_suggestions').insert({
              chat_id: chatId,
              book_id: shareData.book_id,
              suggested_by: user.id,
              status: 'suggested'
            });
          } catch (err: any) {
            // Ignore duplicate errors
            if (err.code !== '23505') console.error('Error adding to suggestions:', err);
          }
        }

        toast({
          title: 'Shared successfully!',
          description: `${shareData.kind} shared to chat`,
        });
      }

      navigate(`/chat/${chatId}`);
      onClose();
    } catch (error) {
      console.error('Error sharing:', error);
      toast({
        title: 'Error',
        description: 'Failed to share content',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const canShare = () => {
    if (activeTab === 'existing') {
      return selectedChat;
    }
    return selectedUsers.length > 0;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {mode === 'share' ? 'Share' : 'New Chat'}
            {shareData && (
              <span className="text-sm font-normal text-muted-foreground ml-2">
                {shareData.book?.title}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="direct">Direct</TabsTrigger>
            <TabsTrigger value="existing">Existing Chat</TabsTrigger>
          </TabsList>

          <TabsContent value="direct" className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search followers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="group-chat"
                checked={isGroup}
                onCheckedChange={(checked) => setIsGroup(checked === true)}
              />
              <Label htmlFor="group-chat">Create group chat</Label>
            </div>

            {isGroup && (
              <>
                <Input
                  placeholder="Group name (optional)"
                  value={groupTitle}
                  onChange={(e) => setGroupTitle(e.target.value)}
                />
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="book-club"
                    checked={isBookClub}
                    onCheckedChange={(checked) => setIsBookClub(checked === true)}
                  />
                  <Label htmlFor="book-club" className="flex items-center gap-2">
                    <BookMarked className="w-4 h-4" />
                    Make this a Book Club
                  </Label>
                </div>
                {isBookClub && shareData?.kind === 'book' && (
                  <p className="text-xs text-muted-foreground">
                    📚 This book will be added to the club's reading list
                  </p>
                )}
              </>
            )}

            <div className="max-h-64 overflow-y-auto space-y-2">
              {filteredFollowers.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  No followers found
                </div>
              ) : (
                filteredFollowers.map((follower) => (
                  <div
                    key={follower.id}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer"
                    onClick={() => handleUserToggle(follower.id)}
                  >
                    <Checkbox
                      checked={selectedUsers.includes(follower.id)}
                      onCheckedChange={() => handleUserToggle(follower.id)}
                    />
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={follower.image || undefined} />
                      <AvatarFallback>
                        {follower.name?.charAt(0) || follower.username.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{follower.name || follower.username}</p>
                      {follower.name && (
                        <p className="text-sm text-muted-foreground">@{follower.username}</p>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {selectedUsers.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedUsers.map((userId) => {
                  const user = followers.find(f => f.id === userId);
                  return (
                    <Badge key={userId} variant="secondary" className="flex items-center gap-1">
                      {user?.name || user?.username}
                      <X
                        className="h-3 w-3 cursor-pointer"
                        onClick={() => handleUserToggle(userId)}
                      />
                    </Badge>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="existing" className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search chats..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="max-h-64 overflow-y-auto space-y-2">
              {filteredChats.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  No chats found
                </div>
              ) : (
                filteredChats.map((chat) => (
                  <div
                    key={chat.id}
                    className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                      selectedChat === chat.id ? 'bg-primary/10' : 'hover:bg-muted/50'
                    }`}
                    onClick={() => setSelectedChat(chat.id)}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback>
                          {chat.is_group ? <Users className="h-4 w-4" /> : <MessageCircle className="h-4 w-4" />}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">
                          {chat.is_group ? (chat.title || 'Group Chat') : getOtherUserName(chat)}
                        </p>
                        {chat.is_group && (
                          <p className="text-sm text-muted-foreground">
                            {chat.members?.length || 0} members
                          </p>
                        )}
                      </div>
                    </div>
                    {selectedChat === chat.id && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </div>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleShare}
            disabled={!canShare() || loading}
          >
            {loading ? (
              <div className="h-4 w-4 animate-spin border-2 border-current border-t-transparent rounded-full mr-2" />
            ) : null}
            {mode === 'share' ? 'Share' : 'Create Chat'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};